import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from '@parcel/ui';
import * as React from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import {
  type BatchRevealTally,
  batchRevealSummary,
  formatCredits,
  needsEmailReveal,
  useRevealContactsBatch,
} from '../../lib/enrich.js';
import { InsufficientCreditsNotice } from '../enrich/credits-notice.js';
import { useEnrichScope } from '../enrich/enrich-scope.js';
import type { ContactRow } from './use-contacts-table.js';

export function accessEmailsPlan(contacts: readonly ContactRow[]): {
  eligible: string[];
  skipped: number;
} {
  const eligible = contacts.filter((contact) => needsEmailReveal(contact)).map((c) => c.id);
  return { eligible, skipped: contacts.length - eligible.length };
}

export function AccessEmailsAction({ selected }: { selected: readonly ContactRow[] }) {
  const { workspaceId, status } = useEnrichScope();
  const batch = useRevealContactsBatch(workspaceId);
  const [open, setOpen] = React.useState(false);
  const [outOfCredits, setOutOfCredits] = React.useState<BatchRevealTally | null>(null);
  const price = status?.enabled ? status.prices.email_reveal : null;
  if (!workspaceId || price == null) return null;

  const { eligible, skipped } = accessEmailsPlan(selected);
  const count = eligible.length;
  const noun = count === 1 ? 'email' : 'emails';

  const run = async () => {
    try {
      const tally = await batch.mutateAsync({ ids: eligible });
      if (tally.insufficient || tally.spendLimited) {
        setOutOfCredits(tally);
        return;
      }
      setOpen(false);
      toast.success(batchRevealSummary(tally));
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't look these up. Try again."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (batch.isPending) return;
        setOpen(next);
        setOutOfCredits(null);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={count === 0}>
          Access emails
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{`Access ${count} ${noun}`}</DialogTitle>
          <DialogDescription>
            {`Up to ${formatCredits(count * price)} (${formatCredits(price)} per email found). You are charged only for emails found.`}
          </DialogDescription>
        </DialogHeader>
        {skipped > 0 ? (
          <p className="text-sm text-muted-foreground">
            {skipped === 1
              ? '1 already has an email or was looked up, and is skipped.'
              : `${skipped} already have an email or were looked up, and are skipped.`}
          </p>
        ) : null}
        {outOfCredits && outOfCredits.found + outOfCredits.missed + outOfCredits.failed > 0 ? (
          <p className="text-sm">{batchRevealSummary(outOfCredits)}</p>
        ) : null}
        {outOfCredits ? (
          <InsufficientCreditsNotice
            reason={outOfCredits.insufficient ? 'credits' : 'spend_limit'}
          />
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={batch.isPending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={batch.isPending || count === 0}
            onClick={() => void run()}
          >
            {batch.isPending ? 'Looking up…' : `Access ${count} ${noun}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
